import { useState } from 'react';
import { wizardIntegrations, WizardIntegration } from '@/mocks/wizard';

interface StepIntegrationsProps {
  selected: string[];
  onChange: (selected: string[]) => void;
}

function IntegrationCard({ intg, isSelected, onToggle }: {
  intg: WizardIntegration;
  isSelected: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      onClick={onToggle}
      className="rounded-xl p-4 flex items-start gap-3 transition-all cursor-pointer"
      style={{
        background: '#fff',
        border: `2px solid ${isSelected ? 'var(--accent)' : '#E5E5E8'}`,
      }}
      onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.borderColor = '#CCCCCC'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = isSelected ? 'var(--accent)' : '#E5E5E8'; }}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: intg.logoBg }}
      >
        <i className={`${intg.logoClass} text-base`} style={{ color: intg.logoColor }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <p className="text-sm font-bold" style={{ color: '#1A1A1A' }}>{intg.name}</p>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {intg.recommended && (
              <span
                className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap"
                style={{ background: 'color-mix(in srgb, var(--err) 10%, transparent)', color: 'var(--accent)', border: '1px solid color-mix(in srgb, var(--accent) 20%, transparent)' }}
              >
                Рекомендовано
              </span>
            )}
            <div
              className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all"
              style={{
                background: isSelected ? 'var(--accent)' : '#F5F5F7',
                border: `1.5px solid ${isSelected ? 'var(--accent)' : '#E5E5E8'}`,
              }}
            >
              {isSelected && <i className="ri-check-line text-[10px]" style={{ color: '#fff' }} />}
            </div>
          </div>
        </div>
        <span
          className="inline-block text-[10px] font-medium px-1.5 py-0.5 rounded mb-1.5"
          style={{ background: '#F5F5F7', color: '#8B8B94' }}
        >
          {intg.category}
        </span>
        <p className="text-xs leading-relaxed" style={{ color: '#6B6B74' }}>{intg.description}</p>
        {intg.connected && (
          <div className="flex items-center gap-1 mt-1.5">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#2E7D32' }} />
            <span className="text-[11px]" style={{ color: '#2E7D32' }}>Вже підключено</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function StepIntegrations({ selected, onChange }: StepIntegrationsProps) {
  const [catFilter, setCatFilter] = useState('Всі');

  const toggle = (id: string) => {
    if (selected.includes(id)) {
      onChange(selected.filter((s) => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const filtered = catFilter === 'Всі'
    ? wizardIntegrations
    : wizardIntegrations.filter((i) => i.category === catFilter);

  const categories = ['Всі', ...Array.from(new Set(wizardIntegrations.map((i) => i.category)))];

  return (
    <div className="max-w-4xl mx-auto w-full">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold mb-2" style={{ color: '#1A1A1A', letterSpacing: '-0.02em' }}>
          Вибір інтеграцій
        </h2>
        <p className="text-sm" style={{ color: '#8B8B94' }}>
          Підключіть зовнішні сервіси для отримання даних та автоматизації
        </p>
      </div>

      {/* Info banner */}
      <div
        className="mb-5 p-3 rounded-lg flex items-start gap-2"
        style={{ background: '#F5F5F7', border: '1px solid #E5E5E8' }}
      >
        <i className="ri-information-2-line text-base mt-0.5 flex-shrink-0" style={{ color: '#8B8B94' }} />
        <p className="text-xs" style={{ color: '#6B6B74' }}>
          Інтеграції можна налаштувати зараз або пізніше у розділі Налаштування → Інтеграції.
          Рекомендовані вже включені за замовчуванням і не потребують API-ключів для базового функціонування.
        </p>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2 mb-5">
        {categories.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => setCatFilter(cat)}
            className="px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-all whitespace-nowrap"
            style={{
              background: catFilter === cat ? '#1A1A1A' : '#F5F5F7',
              color: catFilter === cat ? '#fff' : '#6B6B74',
              border: `1px solid ${catFilter === cat ? '#1A1A1A' : '#E5E5E8'}`,
            }}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        {filtered.map((intg) => (
          <IntegrationCard
            key={intg.id}
            intg={intg}
            isSelected={selected.includes(intg.id) || intg.connected}
            onToggle={() => { if (!intg.connected) toggle(intg.id); }}
          />
        ))}
      </div>

      {/* Summary */}
      <div
        className="flex items-center gap-3 p-4 rounded-xl"
        style={{ background: '#F7F7F9', border: '1px solid #E5E5E8' }}
      >
        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ color: '#2E7D32' }}>
          <i className="ri-plug-line text-lg" />
        </div>
        <div>
          <p className="text-sm font-semibold" style={{ color: '#1A1A1A' }}>
            Обрано інтеграцій: {selected.length}
          </p>
          <p className="text-xs" style={{ color: '#8B8B94' }}>
            Базові інтеграції (ГарПок, Ukrenergo) входять до вартості модуля Dispatch
          </p>
        </div>
      </div>
    </div>
  );
}
